import React, { useState } from "react";
import {
  Alert,
  Modal,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import * as Sentry from "@sentry/react-native";
import * as MailComposer from "expo-mail-composer";
import { AntDesign, Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { useRouter } from "expo-router";

import { type AppUserSession } from "@/schemas/auth/session";
import { UserRestrictionType } from "@/schemas/user/restriction";
import RestrictionService from "@/services/user/restriction";

interface OptionItemProps {
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}

const OptionItem: React.FC<OptionItemProps> = ({ icon, label, onPress }) => (
  <TouchableOpacity style={styles.option} onPress={onPress}>
    {icon}
    <Text style={styles.optionText}>{label}</Text>
  </TouchableOpacity>
);

type HeaderOptionsProps = {
  session: AppUserSession;
  target: string;
  isBlocked: boolean;
  onHandleBlockToggle: (newStatus: boolean) => void;
  iconColor?: string;
  style?: StyleProp<ViewStyle> | undefined;
  showGoToUserProfile?: boolean;
};

export function HeaderOptions({
  session,
  target,
  isBlocked,
  onHandleBlockToggle,
  iconColor = "black",
  style = undefined,
  showGoToUserProfile = false,
}: HeaderOptionsProps): React.JSX.Element {
  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const router = useRouter();

  const toggleMenu = (): void => setMenuVisible(!isMenuVisible);

  const handleReportUser = (): void => {
    toggleMenu();
    const email = "support@getkaiapp.com";
    const subject = `Report User: ${target}`;
    const body =
      "Please provide a brief description of why you are reporting this user.";

    MailComposer.composeAsync({
      recipients: [email],
      subject: subject,
      body: body,
    }).catch((err) => {
      Alert.alert(
        "Report User",
        `Thank you for reporting user ${target}. We will review your report shortly.`,
        [{ text: "OK" }],
      );
      Sentry.captureException(err);
    });
  };

  const handleBlockToggle = (): void => {
    const restrictionService = new RestrictionService(session);
    if (isBlocked) {
      Alert.alert(
        "Unblock User",
        "Unblocking this user will allow them to send you messages. Do you want to continue?",
        [
          {
            text: "Unblock",
            style: "destructive",
            onPress: async (): Promise<void> => {
              try {
                await restrictionService.removeRestriction(
                  target,
                  UserRestrictionType.BLOCKED,
                );
                onHandleBlockToggle(!isBlocked);
              } catch (error) {
                Alert.alert(
                  "Error",
                  "Something went wrong. Please try again later.",
                );
              }
            },
          },
          {
            text: "Cancel",
            style: "cancel",
          },
        ],
        { cancelable: false },
      );
    } else {
      Alert.alert(
        "Block User",
        "Blocking this user will prevent them from sending you messages. Do you want to continue?",
        [
          {
            text: "Block",
            style: "destructive",
            onPress: async (): Promise<void> => {
              try {
                await restrictionService.addRestriction(
                  target,
                  UserRestrictionType.BLOCKED,
                );
                onHandleBlockToggle(!isBlocked);
              } catch (error) {
                Alert.alert(
                  "Error",
                  "Something went wrong. Please try again later.",
                );
              }
            },
          },
          {
            text: "Cancel",
            style: "cancel",
          },
        ],
        { cancelable: false },
      );
    }
  };

  function handleGoToUserProfile(): void {
    toggleMenu();
    router.push({
      pathname: "/(main)/shared/profile",
      params: { username: target },
    });
  }

  return (
    <View>
      <TouchableOpacity hitSlop={8} onPress={toggleMenu} style={style}>
        <MaterialCommunityIcons
          name="dots-vertical"
          size={24}
          color={iconColor}
        />
      </TouchableOpacity>
      <Modal
        visible={isMenuVisible}
        transparent
        animationType="fade"
        onRequestClose={toggleMenu}
      >
        <TouchableOpacity style={styles.modalOverlay} onPress={toggleMenu}>
          <View style={styles.optionsContainer}>
            {
              // TODO: refactor HeaderOptions OR handleGoToUserProfile would be on the header itself later anyways
              showGoToUserProfile && (
                <OptionItem
                  icon={
                    <Feather
                      name="user"
                      size={24}
                      color="black"
                      style={styles.optionIcon}
                    />
                  }
                  label="Go to User Profile"
                  onPress={handleGoToUserProfile}
                />
              )
            }
            <OptionItem
              icon={
                <Feather
                  name="flag"
                  size={24}
                  color="black"
                  style={styles.optionIcon}
                />
              }
              label="Report User"
              onPress={handleReportUser}
            />
            <OptionItem
              icon={
                isBlocked === false ? (
                  <Feather
                    name="user-x"
                    size={24}
                    color="black"
                    style={styles.optionIcon}
                  />
                ) : (
                  <Feather
                    name="user-plus"
                    size={24}
                    color="black"
                    style={styles.optionIcon}
                  />
                )
              }
              label={isBlocked === false ? "Block User" : "Unblock User"}
              onPress={handleBlockToggle}
            />
            <OptionItem
              icon={
                <AntDesign
                  name="close"
                  size={24}
                  color="black"
                  style={styles.optionIcon}
                />
              }
              label="Close Menu"
              onPress={toggleMenu}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  optionsContainer: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    width: "80%",
    maxWidth: 250,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
  },
  optionIcon: {
    marginRight: 16,
  },
  optionText: {
    fontSize: 16,
  },
});
